import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router'

import Button from '@/components/Button'

export default function TermsOfService() {
    const navigate = useNavigate()
    return (
        <div className='min-h-dvh w-full bg-gradient-to-br from-cyan-400 via-cyan-500 to-gray-200'>
            <div className='absolute inset-0 overflow-hidden'>
                <div className='absolute animate-pulse top-10 right-8 w-20 h-20 sm:w-32 sm:h-32 bg-white/10 rounded-2xl rotate-12'></div>
                <div className='absolute animate-pulse bottom-20 left-8 w-24 h-24 bg-white/15 rounded-2xl -rotate-6'></div>
                <div className='absolute animate-pulse top-1/2 right-12 w-16 h-16 bg-yellow-200/25 rounded-xl rotate-45'></div>
            </div>

            <div className='relative z-10 max-w-4xl mx-auto px-4 py-8 sm:py-12'>
                <div className='text-center mb-8 sm:mb-12'>
                    <h1 className='text-3xl sm:text-4xl font-bold text-white mb-4'>BrightPath Terms and Conditions</h1>
                    <p className='text-white/90 text-lg'>Effective Date: May 25, 2026</p>
                    <p className='text-white/80 mt-2'>
                        Welcome to BrightPath! These Terms and Conditions ("Terms") govern your access to and
                        use of the BrightPath website, mobile app, and related services (collectively, the "Platform").
                        By using BrightPath, you agree to be bound by these Terms. Please read them carefully.
                    </p>
                </div>

                <div className='bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10'>
                    <div className='prose prose-gray max-w-none'>
                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    1
                                </span>
                                Who We Are
                            </h2>
                            <p className='text-gray-700 leading-relaxed'>
                                BrightPath ("BrightPath," "we," "us," or "our") is a digital platform developed by
                                BrightPath Inc., a Delaware corporation. Our mission is to help families and caregivers
                                navigate their autism and developmental journey with personalized support tools, curated
                                resources, and AI-powered guidance.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    2
                                </span>
                                Your Responsibilities
                            </h2>
                            <p className='text-gray-700 mb-3'>By using BrightPath, you agree to:</p>
                            <ul className='space-y-2 text-gray-700'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Provide accurate, current information during onboarding.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Use the Platform only for lawful, personal, and non-commercial purposes.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Refrain from attempting to copy, modify, reverse-engineer, or disrupt any part of
                                    the Platform.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Maintain the confidentiality of your account credentials.
                                </li>
                            </ul>
                            <p className='text-gray-700 mt-4'>
                                You must be <strong>at least 18 years old</strong> or have a parent/guardian's consent to
                                use BrightPath. You are responsible for all activities that occur under your account.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    3
                                </span>
                                User Data and Personalization
                            </h2>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                BrightPath collects limited personal information (such as diagnosis, goals, main
                                concerns, your child's birth month and year, and, with your consent, the categories of
                                services your child currently receives) solely to personalize your experience and
                                improve platform functionality.
                            </p>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                We do not require legal names or physical addresses during onboarding. Optional
                                nicknames may be collected for personalization only.
                            </p>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                Your data is never used to train AI models.
                            </p>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                For full details on how we handle your information, please read our Privacy Policy.
                            </p>
                            <p className='text-gray-700 leading-relaxed'>
                                BrightPath takes reasonable steps to protect your information, but no online platform is
                                completely secure. By using BrightPath, you acknowledge and accept this inherent risk.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    4
                                </span>
                                AI-Powered Features and Limitations
                            </h2>
                            <p className='text-gray-700 mb-3'>
                                BrightPath includes AI-driven tools that provide personalized insights, care roadmaps,
                                and emotional support. While we strive for accuracy and relevance, you acknowledge that:
                            </p>
                            <ul className='space-y-2 text-gray-700 mb-3'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    AI-generated responses may occasionally be inaccurate, incomplete, or outdated.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    <span>BrightPath's tools are <strong>informational and supportive only</strong> and are not a substitute for professional advice.</span>
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Decisions affecting your or your child's care should always be made in consultation
                                    with qualified professionals.
                                </li>
                            </ul>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    5
                                </span>
                                No Medical, Clinical, or Professional Advice
                            </h2>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                BrightPath does not provide medical, clinical, psychological, therapeutic, educational,
                                or legal advice.
                            </p>
                            <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-3'>
                                <p className='text-gray-700 leading-relaxed'>
                                    All information, resources, and suggestions available on the Platform are for
                                    informational purposes only.
                                </p>
                            </div>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                Always seek the advice of qualified healthcare providers, educators, or legal
                                professionals regarding any questions or decisions related to diagnosis, treatment, or
                                care.
                            </p>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                Information you share with BrightPath about your child's developmental status,
                                diagnosis, or services received is collected for personalization purposes only.
                                BrightPath is not a healthcare provider, does not maintain medical records, and does
                                not deliver clinical care.
                            </p>
                            <p className='text-gray-700 leading-relaxed'>
                                Reliance on any information provided through BrightPath is solely at your own risk.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    6
                                </span>
                                No Guarantee of Results
                            </h2>
                            <p className='text-gray-700 leading-relaxed'>
                                BrightPath aims to help families and caregivers make more informed and confident
                                decisions, but we make no guarantees or warranties about any specific outcomes,
                                including improvements in developmental progress, care results, or emotional well-being.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    7
                                </span>
                                Intellectual Property
                            </h2>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                All content, branding, design elements, software, and underlying technology of the
                                Platform are the exclusive property of BrightPath Inc. or its licensors and are
                                protected under applicable copyright, trademark, and intellectual property laws.
                            </p>
                            <p className='text-gray-700 leading-relaxed'>
                                You may not reproduce, distribute, modify, or create derivative works from any part of
                                the Platform without written permission from BrightPath Inc.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    8
                                </span>
                                Subscriptions and Payments (If Applicable)
                            </h2>
                            <p className='text-gray-700 mb-3'>
                                Some features of BrightPath may be available through paid subscriptions. When
                                applicable:
                            </p>
                            <ul className='space-y-2 text-gray-700'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Subscription pricing and billing terms will be clearly stated before purchase.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    You may cancel at any time through your profile.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Refunds are provided only as required by law or stated in our refund policy.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    We reserve the right to modify pricing or features, with reasonable notice to
                                    subscribers.
                                </li>
                            </ul>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    9
                                </span>
                                Third-Party Services and Links
                            </h2>
                            <p className='text-gray-700 leading-relaxed'>
                                BrightPath may include links or integrations with third-party tools, providers, or
                                websites. BrightPath is not responsible for their content, accuracy, or privacy
                                practices. Your interactions with these third parties are solely between you and them.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    10
                                </span>
                                Termination and Suspension
                            </h2>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                We reserve the right to suspend or terminate your account at any time, without notice,
                                if you violate these Terms, misuse the Platform, or engage in behavior that harms other
                                users or BrightPath's mission.
                            </p>
                            <p className='text-gray-700 leading-relaxed'>
                                To delete your account, contact{' '}
                                <a
                                    href='mailto:hello@brightpathforautism.com'
                                    className='text-cyan-600 hover:text-cyan-700 underline font-medium'
                                >
                                    hello@brightpathforautism.com
                                </a>
                                . Upon processing your request, data is removed from active databases immediately.
                                Backup copies are purged within 30 days.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    11
                                </span>
                                Limitation of Liability
                            </h2>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                BrightPath is provided "as is" and "as available" without warranties of any kind,
                                express or implied.
                            </p>
                            <div className='bg-gray-50 border border-gray-200 rounded-lg p-4'>
                                <p className='text-gray-700 leading-relaxed mb-2'>
                                    To the maximum extent permitted by law:
                                </p>
                                <ul className='space-y-2 text-gray-700'>
                                    <li className='flex items-start'>
                                        <span className='text-cyan-500 mr-2'>•</span>
                                        BrightPath Inc. and its affiliates, officers, employees, and licensors are not
                                        liable for any indirect, incidental, consequential, or punitive damages arising
                                        from your use or inability to use the Platform.
                                    </li>
                                    <li className='flex items-start'>
                                        <span className='text-cyan-500 mr-2'>•</span>
                                        BrightPath is not responsible for any decisions or outcomes based on AI
                                        suggestions, informational content, or user-generated material.
                                    </li>
                                </ul>
                            </div>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    12
                                </span>
                                Indemnification
                            </h2>
                            <p className='text-gray-700 leading-relaxed'>
                                You agree to indemnify and hold harmless BrightPath Inc., its affiliates, officers,
                                employees, agents, and contractors from and against any claims, liabilities, damages,
                                losses, or expenses (including legal fees) arising out of your use of the Platform, your
                                violation of these Terms, or infringement of any rights of another party.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    13
                                </span>
                                Governing Law and Dispute Resolution
                            </h2>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                These Terms are governed by and construed in accordance with the laws of the State of
                                Delaware, without regard to its conflict of laws principles.
                            </p>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                Any dispute, claim, or controversy arising out of or relating to these Terms or your use
                                of the Platform shall be resolved through binding arbitration administered by the
                                American Arbitration Association.
                            </p>
                            <p className='text-gray-700 leading-relaxed mb-3'>
                                Arbitration will be conducted remotely (via video conference) or, if necessary, in the
                                state where you reside.
                            </p>
                            <p className='text-gray-700 leading-relaxed'>
                                Both you and BrightPath agree to waive any right to a jury trial or class action, and
                                each party will bear its own costs unless otherwise required by law.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    14
                                </span>
                                Changes to These Terms
                            </h2>
                            <p className='text-gray-700 leading-relaxed'>
                                We may update or revise these Terms periodically. If material changes occur, we will
                                notify users via the Platform or email. Continued use of BrightPath after updates means
                                you accept the revised Terms.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    15
                                </span>
                                Contact Us
                            </h2>
                            <p className='text-gray-700 mb-3'>
                                If you have any questions or concerns about these Terms, please contact us at:
                            </p>
                            <div className='bg-cyan-50 border border-cyan-200 rounded-lg p-4'>
                                <p className='text-gray-700'>
                                    <strong>BrightPath Inc.</strong>
                                    <br />
                                    Email:{' '}
                                    <a
                                        href='mailto:hello@brightpathforautism.com'
                                        className='text-cyan-600 hover:text-cyan-700 underline'
                                    >
                                        hello@brightpathforautism.com
                                    </a>
                                    <br />
                                    Website:{' '}
                                    <a
                                        href='https://brightpathforautism.com'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-cyan-600 hover:text-cyan-700 underline'
                                    >
                                        https://brightpathforautism.com
                                    </a>
                                </p>
                            </div>
                        </section>
                    </div>
                </div>

                <div className='text-center mt-8'>
                    <p className='text-white/80 text-sm'>Thank you for choosing BrightPath to support your journey.</p>
                </div>
                <div className='flex  mt-5 w-full justify-center'>
                    <Button
                        onClick={() => navigate(-1)}
                        className='inline-flex items-center px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 transition-colors'
                    >
                        <ArrowLeft className='w-4 h-4 ml-2' />
                        Go Back
                    </Button>
                </div>
            </div>
        </div>
    )
}
